"use client"

import ButtonWithArrow from "@/ui/common/bit/ButtonWithArrow"
import styled from "@emotion/styled"
import { Box, TextField, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { colors, sizes } from "ui/theme/theme"

// Constants
const MAX_RESEND_ATTEMPTS = 2

// OTP Form styles
const StyledOTPContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  text-align: center;
  color: ${colors.black};
`

const StyledOTPTitle = styled(Typography)`
  font-size: 16px;
  margin-bottom: ${sizes.baseSingle};
  color: inherit;
`

const StyledOTPBoldTitle = styled(Typography)`
  font-size: 18px;
  font-weight: 700;
  color: inherit;
  padding-bottom: 32px;
  padding-top: 32px;
`

const StyledOTPInputsContainer = styled(Box)`
  display: flex;
  gap: ${sizes.baseSingle};
  justify-content: center;
  margin-bottom: ${sizes.baseDouble};
`

const StyledOTPInput = styled(TextField)`
  width: 48px;
  height: 48px;

  .MuiOutlinedInput-root {
    width: 48px;
    height: 48px;
  }

  .MuiOutlinedInput-input {
    text-align: center;
    padding: 0;
    height: 48px;
    font-size: 20px;
  }

  &.error .MuiOutlinedInput-root {
    border-color: ${colors.red};
  }
`

const StyledOTPResend = styled(Typography)<{ disabled?: boolean }>`
  cursor: ${(props) => (props.disabled ? "default" : "pointer")};
  color: ${(props) => (props.disabled ? colors.gray : colors.black)};
  margin-bottom: ${sizes.baseDouble};
  text-decoration: ${(props) => (props.disabled ? "none" : "underline")};

  &:hover {
    color: ${(props) => (props.disabled ? colors.gray : colors.darkBlue)};
  }
`

const StyledErrorMessage = styled(Typography)`
  color: ${colors.red};
  margin-bottom: ${sizes.baseSingle};
  font-size: 14px;
`

const StyledButton = styled(ButtonWithArrow)`
  margin: 0 auto;
`

// OTP Form Component Props
export interface OTPFormProps {
  data: any
  phoneNumber: string
  onSubmit: (otp: string) => void
  onResend: () => void
  isSubmitting: boolean
  error?: string
  numberOfDigits?: number
  otpAttempts?: number // Add attempts count prop
}

const OTPForm = ({
  data,
  phoneNumber,
  onSubmit,
  onResend,
  isSubmitting,
  error,
  numberOfDigits = 5,
  otpAttempts = 0,
}: OTPFormProps) => {
  const [otpValues, setOtpValues] = useState<string[]>(
    Array(numberOfDigits).fill(""),
  )
  const inputRefs = useRef<(HTMLInputElement | null)[]>(
    Array(numberOfDigits).fill(null),
  )
  const [resendCount, setResendCount] = useState<number>(0)
  const [isResending, setIsResending] = useState<boolean>(false)
  const [isOtpDisabled, setIsOtpDisabled] = useState<boolean>(false)

  // Check if OTP should be disabled based on attempts or quota exceeded error
  const shouldDisableOtp =
    isOtpDisabled ||
    otpAttempts >= 3 ||
    (error && error.includes("השירות אינו זמין כרגע עקב עומס"))

  const handleResend = async () => {
    if (resendCount >= MAX_RESEND_ATTEMPTS || isResending || isSubmitting)
      return

    setIsResending(true)
    await onResend()
    setResendCount((prevCount) => prevCount + 1)
    setIsResending(false)
  }

  // Scroll to top on mount (no animation)
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo(0, 0)
    }
  }, [])

  // Clear OTP fields when there's an error
  useEffect(() => {
    if (error) {
      setOtpValues(Array(numberOfDigits).fill(""))
      // Focus the first input field
      inputRefs.current[0]?.focus()
    }
  }, [error, numberOfDigits])

  const handleChange = (index: number, value: string) => {
    // Don't allow input if OTP is disabled due to quota exceeded or max attempts
    if (shouldDisableOtp) return

    // Only allow single digits
    if (value.length > 1) return

    const newOtpValues = [...otpValues]
    newOtpValues[index] = value
    setOtpValues(newOtpValues)

    // Auto focus next input if current one is filled
    if (value && index < numberOfDigits - 1) {
      inputRefs.current[index + 1]?.focus()
    }

    // Check if all digits entered and submit
    if (newOtpValues.every((val) => val)) {
      onSubmit(newOtpValues.join(""))
    }
  }

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    // Move focus to previous input on backspace if current is empty
    if (e.key === "Backspace" && !otpValues[index] && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    // Don't allow paste if OTP is disabled due to quota exceeded or max attempts
    if (shouldDisableOtp) {
      e.preventDefault()
      return
    }

    const pasteData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, numberOfDigits)
    if (!pasteData) return

    const newOtpValues = Array(numberOfDigits).fill("")
    for (let i = 0; i < pasteData.length; i++) {
      newOtpValues[i] = pasteData[i]
    }
    setOtpValues(newOtpValues)

    // Focus the next empty input or the last one
    const nextEmptyIndex = newOtpValues.findIndex((val) => !val)
    if (nextEmptyIndex !== -1 && nextEmptyIndex < numberOfDigits) {
      inputRefs.current[nextEmptyIndex]?.focus()
    } else {
      inputRefs.current[numberOfDigits - 1]?.focus()
    }

    // Submit if all filled
    if (newOtpValues.every((val) => val)) {
      onSubmit(newOtpValues.join(""))
    }
  }

  return (
    <StyledOTPContainer>
      <StyledOTPTitle>
        {data.OTPPreText.value}
        <strong>{phoneNumber}</strong>
        {". "}
        {data.oTPPostText.value}
      </StyledOTPTitle>

      <StyledOTPBoldTitle>{data.oTPBoldTitle.value}</StyledOTPBoldTitle>

      {error && <StyledErrorMessage>{error}</StyledErrorMessage>}

      {shouldDisableOtp &&
        !error?.includes("השירות אינו זמין כרגע עקב עומס") && (
          <StyledErrorMessage>
            השירות אינו זמין כרגע עקב עומס. אנא נסה שוב מאוחר יותר.
          </StyledErrorMessage>
        )}

      <StyledOTPInputsContainer style={{ direction: "ltr" }}>
        {Array(numberOfDigits)
          .fill(0)
          .map((_, index) => (
            <StyledOTPInput
              key={index}
              variant="outlined"
              inputRef={(el) => (inputRefs.current[index] = el)}
              className={error ? "error" : ""}
              value={otpValues[index]}
              onChange={(e) => handleChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              autoComplete="one-time-code"
              inputMode="numeric"
              InputProps={{
                inputProps: {
                  maxLength: 1,
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                  style: { textAlign: "center" },
                  autoComplete: "one-time-code",
                },
              }}
              disabled={Boolean(isSubmitting || shouldDisableOtp)}
            />
          ))}
      </StyledOTPInputsContainer>

      {/* <StyledOTPResend // Uncomment if you want to enable resend functionality
        onClick={
          !isResending && !isSubmitting && resendCount < MAX_RESEND_ATTEMPTS
            ? handleResend
            : undefined
        }
        disabled={
          resendCount >= MAX_RESEND_ATTEMPTS || isResending || isSubmitting
        }
      >
        {isResending ? "שולח..." : data.resendText.value}
      </StyledOTPResend> */}

      <StyledButton
        type="button"
        onClick={() => onSubmit(otpValues.join(""))}
        disabled={Boolean(
          otpValues.includes("") || isSubmitting || shouldDisableOtp,
        )}
      >
        {isSubmitting ? "שולח..." : data.oTPButtonText.value}
      </StyledButton>
    </StyledOTPContainer>
  )
}

export default OTPForm
